import { RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { LostBalloon } from '../../illustrations/LostBalloon';
import { cx } from '../../utils/cx';
import { Button } from '../Button/Button';
import s from './EmptyState.module.css';

export type EmptyStateProps = {
  /** An illustration (e.g. <GiftBox />) or emoji. */
  art?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  /** The one obvious next step. */
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
  /** Extra content under the actions, e.g. ChoiceCards. */
  extra?: ReactNode;
  /** page = a whole screen; section = inside a page; inline = a compact row. */
  size?: 'page' | 'section' | 'inline';
  /** Heading level for the title (default 2). */
  headingLevel?: 2 | 3;
  className?: string;
};

/** Never a dead end: say what's going on, and offer one clear next step (docs/05). */
export function EmptyState({
  art,
  title,
  children,
  primaryAction,
  secondaryAction,
  extra,
  size = 'section',
  headingLevel = 2,
  className,
}: EmptyStateProps) {
  const H = `h${headingLevel}` as const;
  return (
    <section className={cx(s.empty, className)} data-size={size}>
      {art ? (
        <div className={s.art} aria-hidden="true">
          {art}
        </div>
      ) : null}
      <div className={s.copy}>
        <H className={s.title}>{title}</H>
        {children ? <div className={s.body}>{children}</div> : null}
      </div>
      {primaryAction || secondaryAction ? (
        <div className={s.actions}>
          {primaryAction}
          {secondaryAction}
        </div>
      ) : null}
      {extra ? <div className={s.extra}>{extra}</div> : null}
    </section>
  );
}

export type ErrorStateProps = {
  title?: ReactNode;
  children?: ReactNode;
  onRetry?: () => void;
  size?: EmptyStateProps['size'];
  className?: string;
};

/** Friendly failure with a retry (docs/05 › Screen specs). */
export function ErrorState({
  title = "Well, that didn't wrap up nicely.",
  children = 'Something went wrong on our end.',
  onRetry,
  size = 'section',
  className,
}: ErrorStateProps) {
  return (
    <div role="alert" className={className}>
      <EmptyState
        size={size}
        art={<LostBalloon size={size === 'page' ? 'md' : 'sm'} />}
        title={title}
        primaryAction={
          onRetry ? (
            <Button variant="secondary" iconStart={RefreshCw} onClick={onRetry}>
              Try again
            </Button>
          ) : undefined
        }
      >
        {children}
      </EmptyState>
    </div>
  );
}
